import type { SimpleUser, SimpleUserRole } from '@/types/simpleUser';

/** Who is operating the POS (distinct from Supabase auth principal). */
export type ActiveOperator =
  | { type: 'member' }
  | { type: 'simple_user'; id: string; username: string; role: SimpleUserRole };

export const MEMBER_OPERATOR: ActiveOperator = { type: 'member' };

export function normalizeSimpleUserRole(value: unknown): SimpleUserRole {
  return value === 'manager' ? 'manager' : 'cashier';
}

export function isSimpleOperator(operator: ActiveOperator | null | undefined): boolean {
  return operator?.type === 'simple_user';
}

export function isMemberOperator(operator: ActiveOperator | null | undefined): boolean {
  return operator?.type === 'member';
}

export function simpleUserFromOperator(
  operator: ActiveOperator | null | undefined,
  organizationId?: string,
): SimpleUser | null {
  if (operator?.type !== 'simple_user') return null;
  return {
    id: operator.id,
    username: operator.username,
    organization_id: organizationId ?? '',
    is_active: true,
    role: normalizeSimpleUserRole(operator.role),
    created_at: '',
    updated_at: '',
  };
}

export function operatorFromSimpleUser(user: SimpleUser): ActiveOperator {
  return {
    type: 'simple_user',
    id: user.id,
    username: user.username,
    role: normalizeSimpleUserRole(user.role),
  };
}

export function parsePersistedOperator(value: unknown): ActiveOperator | null {
  if (!value || typeof value !== 'object') return null;
  const op = value as Record<string, unknown>;
  if (op.type === 'member') return MEMBER_OPERATOR;
  if (
    op.type === 'simple_user' &&
    typeof op.id === 'string' &&
    typeof op.username === 'string'
  ) {
    return {
      type: 'simple_user',
      id: op.id,
      username: op.username,
      role: normalizeSimpleUserRole(op.role),
    };
  }
  return null;
}

export function getOperatorDisplayName(params: {
  activeOperator: ActiveOperator | null | undefined;
  profile?: { full_name?: string | null; username?: string | null } | null;
  fallback: string;
}): string {
  const { activeOperator, profile, fallback } = params;
  if (activeOperator?.type === 'simple_user') {
    return activeOperator.username;
  }
  return profile?.full_name?.trim() || profile?.username?.trim() || fallback;
}
